import { useEffect } from "react";
import type { Map as MlMap, GeoJSONSource } from "maplibre-gl";
import { toLngLat } from "../../../vendor/prism-next/map/index.js";
import { type Route } from "../../../vendor/prism-next/map/logistics/index.js";

const SOURCE_ID = "pn-pickup-marker-src";
const LAYER_ID = "pn-pickup-marker-layer";
const ICON_ID = "pn-pickup-marker-icon";

// lucide `Warehouse` (lucide-react v1.25.0) — the SVG path data, on lucide's 24×24
// viewBox. Rendered via Path2D rather than the React component so it can rasterize
// to a canvas for a MapLibre SDF symbol. Refresh these if the vendored lucide bumps.
const WAREHOUSE_PATHS = [
  "M18 21V10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v11",
  "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 1.132-1.803l7.95-3.974a2 2 0 0 1 1.837 0l7.948 3.974A2 2 0 0 1 22 8z",
  "M6 13h12",
  "M6 17h12",
] as const;

/** Rasterize lucide's Warehouse (a stroke icon) to `size`px as a finished, coloured
 *  image — a dark semi-transparent halo pass under a white glyph pass, so it reads on
 *  the route-coloured pickup circle in either theme. NOT an SDF: SDF wants solid
 *  shapes, and a thin stroke's alpha never crosses the distance-field threshold (it
 *  rendered blank). A plain raster shows its own pixels directly; pixelRatio keeps it
 *  crisp on retina. The baked #ffffff / dark colours are rendered marker colours that
 *  can't be CSS tokens (WebGL paint) — the accepted hardcoded-hex exception. */
function buildWarehouseIcon(size: number): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(size / 24, size / 24); // lucide viewBox is 24×24
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const paths = WAREHOUSE_PATHS.map((d) => new Path2D(d));
  // halo pass (dark, wider) — edge definition on any background
  ctx.strokeStyle = "rgba(15,23,42,0.55)";
  ctx.lineWidth = 4.5;
  for (const path of paths) ctx.stroke(path);
  // glyph pass (white, on top)
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2.25;
  for (const path of paths) ctx.stroke(path);
  return ctx.getImageData(0, 0, size, size);
}

function toFeatureCollection(routes: Route[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: routes
      .filter((r) => r.coords.length > 0)
      .map((r) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: toLngLat(r.coords[0]) },
        properties: {},
      })),
  };
}

/**
 * prism-next's `RouteOverlay` draws the pickup stop as a plain filled circle in the
 * route colour and exposes no pickup-icon prop, so this app layer overlays a white
 * lucide Warehouse glyph on top of it (the depot origin) — a white-on-coloured-disc
 * marker that reads in both themes; the route colour stays carried by prism-next's
 * circle underneath. This is the one map mechanism prism-next doesn't cover.
 */
export function usePickupMarkerLayer(map: MlMap | null, routes: Route[]): void {
  useEffect(() => {
    if (!map) return;

    const ensureLayer = (): void => {
      // Plain raster (not SDF): the image already carries its final colours.
      if (!map.hasImage(ICON_ID)) map.addImage(ICON_ID, buildWarehouseIcon(128), { pixelRatio: 4 });
      if (map.getSource(SOURCE_ID)) return;
      map.addSource(SOURCE_ID, { type: "geojson", data: toFeatureCollection(routes) });
      map.addLayer({
        id: LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        layout: {
          "icon-image": ICON_ID,
          "icon-size": 0.62, // ~19px — sits on prism-next's r=12 pickup circle
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });
    };

    if (map.isStyleLoaded()) ensureLayer();
    else map.once("load", ensureLayer);

    return () => {
      if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Data updates (route list changes). Colour no longer varies per route, but the
  // marker positions do, so keep the source in sync when routes change.
  useEffect(() => {
    if (!map) return;
    const source = map.getSource(SOURCE_ID);
    if (source && "setData" in source) (source as GeoJSONSource).setData(toFeatureCollection(routes));
  }, [map, routes]);
}
